/**
 * Where the identity provider's two consoles are, for this deployment.
 *
 * Two different pages, for two different people:
 *
 *  * the ACCOUNT console -- a signed-in person's own page, where they
 *    change their password and see their sessions. It is part of the
 *    application realm, served on the same address as the sign-in form,
 *    and needs nothing beyond the session they already have;
 *  * the ADMIN console -- where an identity administrator manages other
 *    people. Since round 15 K3 the hosted edge refuses it on the public
 *    address (`/auth/admin` and the master realm answer 404); its
 *    operator reaches it over the tailnet, through a tunnel.
 *
 * The Users page built its "Manage" links on the public address anyway,
 * so on the host every one of them opened on the edge's refusal -- found
 * by an administrator looking for where to change his own password,
 * which was never the admin console's job.
 *
 * `VITE_KEYCLOAK_ADMIN_URL` says where the admin console is served. Unset,
 * it is where the sign-in form is (a laptop's stack, which proxies it).
 * `none` -- the hosted build sets it -- means "not on any address this
 * browser can open", and the page says so instead of linking to a
 * refusal.
 */

function trimmed(url: string): string {
  return url.replace(/\/$/, "");
}

/** The base the admin console is served under, or null where it is not. */
export function adminConsoleBase(
  env: Record<string, string | undefined> = import.meta.env,
): string | null {
  const configured = (env.VITE_KEYCLOAK_ADMIN_URL ?? "").trim();
  if (configured.toLowerCase() === "none") return null;
  const base = configured || env.VITE_KEYCLOAK_URL;
  return base ? trimmed(base) : null;
}

/** True where the deployment says its admin console is not served here. */
export function adminConsoleWithheld(
  env: Record<string, string | undefined> = import.meta.env,
): boolean {
  return (env.VITE_KEYCLOAK_ADMIN_URL ?? "").trim().toLowerCase() === "none";
}

/** The admin console's user list for the application realm. */
export function adminUsersUrl(
  env: Record<string, string | undefined> = import.meta.env,
): string | null {
  const base = adminConsoleBase(env);
  const realm = env.VITE_KEYCLOAK_REALM;
  if (!base || !realm) return null;
  return `${base}/admin/master/console/#/${realm}/users`;
}

/** The signed-in person's own account page: password, sessions. */
export function accountConsoleUrl(
  env: Record<string, string | undefined> = import.meta.env,
): string | null {
  const url = env.VITE_KEYCLOAK_URL;
  const realm = env.VITE_KEYCLOAK_REALM;
  if (!url || !realm) return null;
  return `${trimmed(url)}/realms/${encodeURIComponent(realm)}/account`;
}
